import { useState } from "react"
import { t } from "@lingui/core/macro"
import { BroadcastStatus } from "@multimediallc/web-utils"
import { ShortcodeParser } from "@multimediallc/web-utils/shortcodeParsing"
import { Shortcode } from "@multimediallc/web-utils/types"
import { useAppContext } from "../../../../hooks/appContext"
import { normalizeResource } from "../../../../routes/util"
import { addPageAction } from "../../../../utils/newrelic"
import { ConfirmModal } from "../../../common/molecules/ConfirmModal"
import { Alignment } from "../../../common/molecules/ConfirmModal/ConfirmModal"
import { useMobileBroadcastContext } from "../../mobileBroadcastContext"
import type { IShortcodePart, IUserInfo } from "@multimediallc/web-utils/types"
import "./ShortcodePart.scss"

type ActualShortcodeProps = IShortcodePart & {
    fromUser?: IUserInfo
}

export function ShortcodePart(props: ActualShortcodeProps) {
    const { code, msg, signupLink, amt, fromUser } = props
    const { room, broadcastStatus } = useMobileBroadcastContext()
    const language = useAppContext().context.language_code
    // TODO (CBD-1226) ensure this plays well with MobileStreamer's beforeunload handler
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [pendingUrl, setPendingUrl] = useState<string>("")

    const handleNavigation = () => {
        window.open(pendingUrl, "_self")
        const isMessage = fromUser !== undefined
        const shortCodeAction = {
            from_user: isMessage ? fromUser.username : "",
            shortcode: code,
            room: room,
            user_clicked: room,
            tip: amt !== undefined && code === Shortcode.Tip ? amt : 0,
            message: msg,
            from_notice: !isMessage,
        }
        addPageAction("MobileBroadcastShortcodeLinkClicked", shortCodeAction)
        setShowConfirmModal(false)
    }

    const confirmNavigation = (
        e: React.MouseEvent<HTMLAnchorElement>,
    ): void => {
        e.preventDefault()
        const anchor = e.currentTarget as HTMLAnchorElement
        setPendingUrl(anchor.href)
        if (broadcastStatus === BroadcastStatus.Offline) {
            window.open(anchor.href, "_self")
        } else {
            setShowConfirmModal(true)
        }
    }
    let linkText, linkUrl
    let callback: ((e: React.MouseEvent<HTMLAnchorElement>) => void) | undefined
    switch (code) {
        case Shortcode.Fanclub:
            linkText = t`Join ${room}'s Fan Club`
            linkUrl = signupLink
            callback = confirmNavigation
            break
        case Shortcode.Supporter:
            linkText = t`Become a supporter`
            linkUrl = "/supporter/upgrade/"
            callback = confirmNavigation
            break
        case Shortcode.Signup:
            linkText = t`Join Chaturbate`
            linkUrl = signupLink
            callback = confirmNavigation
            break
        // No callback for Tip or Follow shortcodes, no effect for broadcaster
        case Shortcode.Tip:
            linkText = msg ?? ""
            break
        case Shortcode.Follow:
            linkText = t`Follow ${room}`
            break
        case Shortcode.Help: // Only relevant for outgoing messages
        default:
            return null
    }

    return (
        <>
            <a
                href={linkUrl && normalizeResource(language, linkUrl)}
                className="shortcode-link"
                title={ShortcodeParser.getShortcodeTitle(props)}
                data-testid="shortcode-link"
                onClick={(e) => {
                    if (callback) {
                        callback(e)
                    }
                }}
            >
                {linkText}
            </a>
            {showConfirmModal && (
                <ConfirmModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleNavigation}
                    message={t`Navigating away from this page will immediately stop your broadcast.`}
                    title={t`Are you sure?`}
                    confirmText={t`Continue`}
                    cancelText={t`Cancel`}
                    textAlign={Alignment.left}
                />
            )}
        </>
    )
}
